/* eslint-disable no-useless-catch */
// src/stores/authStore.ts
import { isAxiosError } from "axios";
import { getProfileAction } from "@/modules/login/actions/getProfileAction";
import { loginAction } from "@/modules/login/actions/loginAction";
import { refreshSessionAction } from "@/modules/login/actions/refreshSessionAction";
import { User } from "@/modules/login/interfaces/userProfile.interface";
import { defineStore } from "pinia";
import { ref, computed } from "vue";

/** Intervalo de renovación JWT (ms). Debe ser menor que la vida del token en el backend. */
const SESSION_REFRESH_MS = Number(process.env.VUE_APP_AUTH_REFRESH_MS) || 5 * 60 * 1000;

export const useAuthStore = defineStore("auth", () => {
  let sessionRefreshTimer: ReturnType<typeof setInterval> | null = null;

  // State
  const token = ref<string | null>(localStorage.getItem("access_token"));
  const user = ref<User | null>(null);
  const isLoading = ref(false);
  const isInitialized = ref(false);

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value);

  // Actions
  async function login(identifier: string, password: string) {
    isLoading.value = true;
    try {
      const response = await loginAction(identifier, password);
      
      const accessToken = response.data.data.access_token;
      
      // 1. Guardar en localStorage
      localStorage.setItem("access_token", accessToken);
      
      // 2. Asignar a la variable reactiva
      token.value = accessToken;
      
      // 3. Obtener perfil
      await fetchProfile();
      startSessionKeepAlive();

      return response;
    } catch (error) {
      
      // Limpiar todo si falla
      token.value = null;
      user.value = null;
      localStorage.removeItem("access_token");
      
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  async function fetchProfile() {
    const currentToken = localStorage.getItem("access_token");

    if (!currentToken) {
      logout();
      throw new Error("No token available");
    }

    try {
      const response = await getProfileAction();
      user.value = response.data.data.user;
      return response;
    } catch (error) {
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          logout();
        }
      }
      throw error;
    }
  }

  function stopSessionKeepAlive() {
    if (sessionRefreshTimer) {
      clearInterval(sessionRefreshTimer);
      sessionRefreshTimer = null;
    }
  }

  async function tickRefreshSession() {
    if (!localStorage.getItem("access_token") || !token.value) {
      stopSessionKeepAlive();
      return;
    }
    try {
      const newToken = await refreshSessionAction();
      setToken(newToken);
    } catch {
      /* 401 lo maneja el interceptor; fallos de red no cierran sesión */
    }
  }

  function startSessionKeepAlive() {
    stopSessionKeepAlive();
    if (!token.value) return;
    void tickRefreshSession();
    sessionRefreshTimer = setInterval(() => {
      void tickRefreshSession();
    }, SESSION_REFRESH_MS);
  }

  // NUEVA FUNCIÃ“N: Inicializar el store al cargar la app
  async function initializeAuth() {
    // Si ya se inicializÃ³, no hacer nada
    if (isInitialized.value) {
      return;
    }

    const storedToken = localStorage.getItem("access_token");
    
    // Si hay token pero no hay usuario, obtener el perfil
    if (storedToken && !user.value) {
      isLoading.value = true;
      try {
        token.value = storedToken;
        await fetchProfile();
        startSessionKeepAlive();
      } catch {
        /* fetchProfile ya hace logout solo en 401/403 */
      } finally {
        isLoading.value = false;
        isInitialized.value = true;
      }
    } else {
      isInitialized.value = true;
    }
  }

  function logout() {
    stopSessionKeepAlive();
    token.value = null;
    user.value = null;
    localStorage.removeItem("access_token");
  }

  function setToken(newToken: string) {
    localStorage.setItem("access_token", newToken);
    token.value = newToken;
  }

  return {
    // State
    token,
    user,
    isLoading,
    isInitialized,

    // Getters
    isAuthenticated,

    // Actions
    login,
    fetchProfile,
    logout,
    setToken,
    initializeAuth, // Nueva acciÃ³n
  };
});
